import React, { useState, useEffect } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { connect } from 'react-redux';
import { LinearProgress, Button, Typography, Table, TextField, Tooltip, Grid } from '@mui/material';
import { Search, HelpOutline } from '@mui/icons-material';
import { Page, Row, Fill } from '../util/Page';
import {
  createExportAction,
  modifyExportAction,
  refreshExportLinksAction,
  selectPointsAction,
} from '../../redux/actions/exports';
import { getExportDownloadLink } from '../../api/Api';
import { timestampOrDate } from '../../utils/parsing';
import { useTimer, useIdleDetector } from '../util/timer';
import ExportsLoader from '../loaders/ExportsLoader';
import BuildingsLoader from '../loaders/BuildingsLoader';
import BuildingSelector from '../buildingselector/BuildingSelector';
import EquipTypeSelector from '../pointMgmt/EquipTypeSelector';
import PointTypeSelector from '../ingestQa/PointTypeSelector';
import { UnconnectedEquipmentSelector as EquipmentSelector } from '../pointMgmt/EquipmentSelector';
import RangePicker, { daysAgo } from '../trends/RangePicker';
import { UnitPrefLoader } from '../account/unitPrefs/UnitPreferences';
import QueryUnitPrefs from '../account/unitPrefs/QueryUnitPrefs';
import { SelectorLoader, useLiveSelector } from './LiveSelector';
import FormatSelector from './FormatSelector';
import ClickableList from './ClickableList';
import PreviewStats from './PreviewStats';
import AggPrefsTable from './AggPrefsTable';

const EditorWrapper = ({ children }) => (
  <div style={{ display: 'grid' }} className="export-editor">
    <Page>
      <Row>
        <Button component={Link} to="/exports">
          &lt; Back to Exports List
        </Button>
        <Fill />
      </Row>
    </Page>
    <BuildingsLoader />
    {children}
  </div>
);

const both = (f1, f2) => (val) => f1(val) && f2(val);

// delightfully, javascript can pass a `Date` object of 'Invalid Date' which
// is non-null but returns NaN from getTime()
const timestampOrNow = (d) => {
  const stamp = d.getTime();
  if (!Number.isNaN(stamp)) return stamp;
  return new Date().getTime();
};

// Error messages from pydantic look like this:
//   ('end',): End time must be greater than start time (value_error)
const cleanError = (err) => err.replace(/^\(.+\):\s+/, '');

/* eslint-disable jsx-a11y/label-has-associated-control */

const ExportEditor = ({
  loaded,
  exportsById,
  buildingsById,
  equipTypesById,
  pointTypesById,
  equipById,
  create,
  save,
  refreshLinks,
  selectPoints,
}) => {
  const [format, setFormat] = useState('csv');
  const [changes, setChanges] = useState({});
  const [preview, setPreview] = useState({});
  const match = useMatch('/exports/edit/:exportId');
  const { exportId: exportIdProp } = match?.params ?? {};
  const [exportId, setExportId] = useState(exportIdProp);
  const [isIdle, activity] = useIdleDetector({ thresholdSeconds: 2 });
  const [error, setError] = useState('');

  // used to highlight the broken field in the form
  const isErrorField = (field) => error.startsWith(`('${field}',)`);

  // the id may be set by this component in the "new" case
  const tsExport = exportsById[exportId] || {};
  // flatten out the export's json format to get at the selector fields easier
  const flatExport = { ...tsExport, ...tsExport.query, ...tsExport.query?.selector };
  const getField = (key, defaultVal = '') => {
    if (changes[key] != null) return changes[key];
    if (flatExport && flatExport[key] != null) return flatExport[key];
    return defaultVal;
  };
  const setField = (key, value) => {
    setChanges({ ...changes, [key]: value });
    activity();
    setError('');
  };
  const change = (key) => (e) => setField(key, e.target.value);

  const pendingChanges = Object.values(changes).length > 0;
  const needsPreview = pendingChanges || Object.values(preview).length === 0;
  const counts = preview?.counts ?? {};
  const matchedPoints = counts.points > 0;

  const links = tsExport?.links ?? [];
  const now = new Date().getTime();
  const unexpired = links.find(({ expires }) => expires > now);
  const token = unexpired?.token;
  const filename = `${getField('name') || 'onboard-export'}.${format}`;

  const buildings = getField('buildings', []);
  const equipmentTypes = getField('equipment_types', []);
  const pointTypes = getField('point_types', []);
  const equipment = getField('equipment', []);
  const units = getField('units', {});
  const unitPrefs = Object.entries(units).map(([m, u]) => ({ measurement_id: m, unit_id: u }));
  const aggregations = getField('aggregations', {});
  const aggPrefs = Object.entries(aggregations).map(([m, agg]) => ({ measurement_id: m, agg }));
  const buildingNameMaybe = (equip) => {
    const building = buildingsById[equip.buildingId];
    if (buildings.length > 1 && building != null) {
      return ` in ${building.name}`;
    }
    return '';
  };
  const equipDisplayName = (e) => `${e.typeAbbr} ${e.suffix} (${e.id}${buildingNameMaybe(e)})`;
  const samplingMins = getField('sampling_mins', 5);
  const start = timestampOrDate(getField('start', daysAgo(7)));
  const end = timestampOrDate(getField('end', new Date()));

  const buildSelector = () => ({
    buildings,
    equipment_types: equipmentTypes,
    equipment,
    point_types: pointTypes,
  });
  const selector = buildSelector();
  const buildReq = () => ({
    // export fields
    name: getField('name'),
    description: getField('description'),
    sampling_mins: samplingMins,
    // timeseries query fields
    start: timestampOrNow(start),
    end: timestampOrNow(end),
    selector,
    units,
    aggregations,
  });

  const [loading, filters, filterCounts, onSelectorChange] = useLiveSelector({ selectPoints });
  // update the counts when the selector changes (after the page has loaded)
  useEffect(() => {
    if (loaded) {
      onSelectorChange(selector);
    }
  }, [loaded, JSON.stringify(selector)]);
  const countsMaybe = (field) => {
    const count = filterCounts[field];
    if (count != null) {
      return ` ${count}`;
    }
    return '';
  };

  // check and see if we need to refresh the links because they're all expired
  const maybeRefreshLinks = () => {
    if (exportId != null && !token) {
      refreshLinks(exportId);
    }
  };
  const saveExport = () => {
    if (!pendingChanges || error !== '') {
      return Promise.resolve('skipped');
    }
    const handler = exportId != null ? (r) => save(exportId, r) : create;
    return handler(buildReq())
      .then((res) => {
        setExportId(res.data.id);
        setChanges({});
        setError('');
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        try {
          const { description } = JSON.parse(err.message);
          setError(description);
        } catch {
          setError('Something went wrong, please refresh the page and try again');
        }
      });
  };
  // save the export fields ~2 seconds after the user stops editing
  useTimer({ fireSeconds: 1, onFire: () => isIdle && saveExport() });
  /**
   * Force the user to explicitly try out their query before a download link is available
   */
  const runPointSelection = () => {
    saveExport();
    selectPoints(buildSelector())
      .then((res) =>
        setPreview({
          counts: {
            buildings: res.buildings.length,
            equipment: res.equipment.length,
            equipmentTypes: res.equipment_types.length,
            pointTypes: res.point_types.length,
            points: res.points.length,
          },
          equipmentIds: res.equipment,
          buildings: res.buildings,
        })
      )
      .then(maybeRefreshLinks);
  };

  if (!loaded) {
    return (
      <EditorWrapper>
        <Page>
          <Row>
            <div className="loading">
              <LinearProgress color="secondary" className="progress" />
              <ExportsLoader />
            </div>
          </Row>
        </Page>
      </EditorWrapper>
    );
  }

  const leftItemXs = 3;
  const rightItemXs = 9;

  return (
    <EditorWrapper>
      <Page>
        <Row>
          <Typography>Export options</Typography>
        </Row>
        {error !== '' ? (
          <Row>
            <Typography>
              We had a problem saving your export, please check the form and try again:&nbsp;
            </Typography>
            <Typography color="error">{cleanError(error)}</Typography>
          </Row>
        ) : null}
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Name</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <TextField
                id="name"
                error={isErrorField('name')}
                placeholder="An optional name to refer to this export"
                variant="outlined"
                size="small"
                fullWidth
                value={getField('name')}
                onChange={change('name')}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Description</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <TextField
                id="description"
                error={isErrorField('description')}
                placeholder="An optional description of the data in this export"
                variant="outlined"
                size="small"
                fullWidth
                value={getField('description')}
                onChange={change('description')}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Time range</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <RangePicker
                error={isErrorField('start') || isErrorField('end')}
                from={start}
                to={end}
                onChange={({ from: newStart, to: newEnd }) => {
                  setChanges({ ...changes, start: newStart, end: newEnd });
                  setError('');
                }}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Sampling rate</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <TextField
                type="number"
                inputProps={{ min: 1 }}
                label="minutes"
                size="small"
                variant="outlined"
                value={samplingMins}
                onChange={change('sampling_mins')}
                error={isErrorField('sampling_mins')}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <UnitPrefLoader />
          <Grid container>
            <Grid item xs={leftItemXs}>
              <Row>
                <Typography>Unit preferences</Typography>
                <Tooltip title="These preferences apply to this data export, and override all other preferences. Global preferences can be found on the account page.">
                  <HelpOutline />
                </Tooltip>
              </Row>
            </Grid>
            <Grid item xs={rightItemXs}>
              <Table>
                <QueryUnitPrefs
                  preferences={unitPrefs}
                  setPreference={(mid, uid) => {
                    setField('units', { ...units, [mid]: uid });
                    return Promise.resolve('ok');
                  }}
                  deletePreference={(mid) => {
                    const newUnits = { ...units };
                    delete newUnits[mid];
                    setField('units', newUnits);
                    return Promise.resolve('ok');
                  }}
                />
              </Table>
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <Row>
                <Typography>Resampling aggregations</Typography>
                <Tooltip title="These preferences control how data is aggregated when resampling.">
                  <HelpOutline />
                </Tooltip>
              </Row>
            </Grid>
            <Grid item xs={rightItemXs}>
              <AggPrefsTable
                preferences={aggPrefs}
                setPreference={(mid, agg) => {
                  setField('aggregations', { ...aggregations, [mid]: agg });
                  return Promise.resolve('ok');
                }}
                deletePreference={(mid) => {
                  const newAggs = { ...aggregations };
                  delete newAggs[mid];
                  setField('aggregations', newAggs);
                  return Promise.resolve('ok');
                }}
              />
            </Grid>
          </Grid>
        </Row>
      </Page>
      <Page>
        <SelectorLoader buildings={buildings} />
        <Row>
          <Typography>Data Selection</Typography>
          <Tooltip title="Each selector row allows you to refine what data show up in your export. The constraints are added together, so for example selecting an equipment type will limit which point types and equipment are in the export. The counts indicate how many options are available based on the other selected constraints. Selecting additional options in one row will, in general, increase the options in other rows.">
            <HelpOutline />
          </Tooltip>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Buildings</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <ClickableList
                values={buildings}
                valueLookup={buildingsById}
                onClick={(id) =>
                  setField(
                    'buildings',
                    buildings.filter((bid) => bid !== id)
                  )
                }
                emptyComponent={
                  <Typography color="error">
                    No explicit buildings added yet, please add one or more to continue
                  </Typography>
                }
              />
              {/* note: intentionally dont filter the buildings selector from the live selection */}
              <BuildingSelector
                selectedBuildingId=""
                filter={(b) => b.id > 0 && buildings.indexOf(b.id) === -1}
                handleSelection={(e) => setField('buildings', [...buildings, e.target.value])}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Equipment Types</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <ClickableList
                values={equipmentTypes}
                nameField="nameLong"
                valueLookup={equipTypesById}
                onClick={(id) =>
                  setField(
                    'equipment_types',
                    equipmentTypes.filter((etid) => etid !== id)
                  )
                }
                emptyComponent={
                  <Typography>
                    No explicit Equipment Types added yet, query all{countsMaybe('equipment_types')}{' '}
                    matching types
                  </Typography>
                }
              />
              <EquipTypeSelector
                disabled={loading.equipment_types}
                selected=""
                filter={both(filters.equipment_types, (et) => equipmentTypes.indexOf(et.id) === -1)}
                onChange={(etId) => setField('equipment_types', [...equipmentTypes, etId])}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Point Types</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <ClickableList
                values={pointTypes}
                valueLookup={pointTypesById}
                onClick={(id) =>
                  setField(
                    'point_types',
                    pointTypes.filter((ptid) => ptid !== id)
                  )
                }
                emptyComponent={
                  <Typography>
                    No explicit Point Types added yet, query all{countsMaybe('point_types')}{' '}
                    matching types
                  </Typography>
                }
              />
              <PointTypeSelector
                disabled={loading.point_types}
                selected=""
                filter={both(filters.point_types, (pt) => pointTypes.indexOf(pt.id) === -1)}
                onChange={(ptId) => setField('point_types', [...pointTypes, ptId])}
              />
            </Grid>
          </Grid>
        </Row>
        <Row>
          <Grid container>
            <Grid item xs={leftItemXs}>
              <label>Equipment</label>
            </Grid>
            <Grid item xs={rightItemXs}>
              <ClickableList
                values={equipment}
                valueLookup={equipById}
                nameRenderer={equipDisplayName}
                onClick={(id) =>
                  setField(
                    'equipment',
                    equipment.filter((eid) => eid !== id)
                  )
                }
                emptyComponent={
                  <Typography>
                    {buildings.length === 0
                      ? 'Please select one or more buildings in order to load equipment'
                      : `No explicit Equipment added yet, query all${countsMaybe(
                          'equipment'
                        )} matching equipment in ${buildings.length} selected building(s)`}
                  </Typography>
                }
              />
              <EquipmentSelector
                disabled={loading.equipment || buildings.length === 0}
                equipment={Object.values(equipById)}
                displayNameRender={equipDisplayName}
                selected=""
                filter={both(filters.equipment, (e) => equipment.indexOf(e.id) === -1)}
                onValueChange={(equipId) => setField('equipment', [...equipment, equipId])}
              />
            </Grid>
          </Grid>
        </Row>
      </Page>
      <Page>
        {needsPreview ? (
          <>
            <Row>
              <Typography>Preview the data selection to generate a download link</Typography>
            </Row>
            <Row>
              <Button
                disabled={buildings.length === 0}
                startIcon={<Search />}
                variant="contained"
                onClick={runPointSelection}
              >
                Preview Query
              </Button>
            </Row>
          </>
        ) : (
          <>
            <Row>
              <div>Preview Stats</div>
            </Row>
            {!matchedPoints ? (
              <Row>
                <Typography color="error">
                  Your data selection did not match any points in the chosen buildings
                </Typography>
              </Row>
            ) : (
              <Row>
                <PreviewStats preview={preview} />
              </Row>
            )}
          </>
        )}
      </Page>
      {!needsPreview && matchedPoints ? (
        <Page>
          <Row>
            <Typography>Choose an output format and download</Typography>
          </Row>
          <Row>
            <FormatSelector selected={format} onChange={setFormat} />
          </Row>
          <Row>
            <a href={getExportDownloadLink(token, format)} download={filename}>
              Click here to export data as {format}
            </a>
          </Row>
        </Page>
      ) : null}
    </EditorWrapper>
  );
};

export default connect(
  ({
    entities: {
      buildings: { byId: buildingsById },
      equipTypes: { byId: equipTypesById },
      equipment: { byId: equipById },
      pointTypes: { byId: pointTypesById },
      tsExports: { byId: exportsById, loaded },
    },
  }) => ({ loaded, exportsById, buildingsById, equipTypesById, pointTypesById, equipById }),
  (dispatch) => ({
    create: (req) => dispatch(createExportAction(req)),
    save: (id, req) => dispatch(modifyExportAction(id, req)),
    refreshLinks: (id) => dispatch(refreshExportLinksAction(id)),
    selectPoints: (selector) => dispatch(selectPointsAction(selector)),
  })
)(ExportEditor);
